import { ReparoApi } from '@api';
import { LaneEditModal } from '@components/containers/lanes/LaneEditModal';
import {
    buildLaneColumns,
    type LaneRow,
} from '@components/containers/lanes/laneColumns';
import VendorProductionCapacity from '@components/containers/suppliers/VendorProductionCapacity';
import { VendorCapabilitiesField } from '@components/containers/suppliers/VendorCapabilitiesField';
import { VendorPoPrefixField } from '@components/containers/suppliers/VendorPoPrefixField';
import type { VendorCapability } from '@api/vendors';
import { useCbmEnabled } from '@hooks/useCbmEnabled';
import { Button } from '@mantine/core';
import { MetricStrip } from '@components/MissionControl';
import {
    SUPPLIER_LIST_METRICS,
    VENDOR_PANE_METRICS,
} from './suppliers.metrics';
import PageHeader from '@components/common/PageHeader';
import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import EntityTagStrip from '@components/ui/EntityTagStrip';
import {
    DataSheet,
    DataSheetPagination,
    DataSheetStatusBadge,
    type DataSheetColumn,
} from '@components/ui/DataSheet';
import FilterDropdown, { type DropdownOption } from '@components/ui/Dropdown';
import KpiCard from '@components/ui/KpiCard';
import SegmentedToggle from '@components/ui/SegmentedToggle';
import ShareModal from '@components/ui/ShareModal';
import { Export, XCircle } from '@phosphor-icons/react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useSearchParams } from 'react-router';
import { ConsultantCapability } from '../../constants/consultant-capability';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useWidgetContextCapture } from '@hooks/useWidgetContextCapture';
import PurchaseOrderDetailDrawer, {
    type PurchaseOrderRow,
} from './PurchaseOrderDetailDrawer';
import { formatCount } from '@utils/currency';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface VendorApiResponse {
    vendorId?: string;
    name?: string;
    country?: string;
    city?: string;
    type?: string;
    contactPerson?: string;
    email?: string;
    minLeadTime?: number | null;
    maxLeadTime?: number | null;
    categories?: string[] | null;
    paymentTerms?: string | null;
    isActive?: boolean | null;
    vendorRole?: string | null;
    lastOrderDate?: string | null;
    openOrderCount?: number | null;
}

interface VendorDetailApiResponse {
    vendorId?: string;
    name?: string;
    country?: string;
    city?: string;
    type?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    secondaryContact?: string | null;
    secondaryEmail?: string | null;
    minLeadTime?: number | null;
    maxLeadTime?: number | null;
    vendorCode?: string | null;
    vendorRole?: string | null;
    categories?: string[] | null;
    paymentTerms?: string | null;
    moq?: number | null;
    incoterms?: string | null;
    capabilities?: string[] | null;
    poNumberPrefix?: string | null;
    dailyProductionCapacity?: number | null;
    workingDays?: number[] | null;
}

interface VendorRow {
    vendorId: string;
    name: string;
    country: string;
    city?: string;
    minLeadTime: number | null;
    maxLeadTime: number | null;
    type: string;
    contactPerson: string;
    email?: string;
    categories?: string[] | null;
    paymentTerms?: string | null;
    isActive?: boolean | null;
    vendorRole?: string | null;
    lastOrderDate?: string | null;
    openOrderCount?: number | null;
}

type POStatus = string;

interface VendorPoRow {
    id: string;
    poNumber: string;
    orderDate: string;
    status: POStatus;
    itemsCount: number;
    totalQuantity: number;
    expiryDate?: string;
    benchmarkDate?: string;
    actualDeliveryDate?: string;
    varianceDays?: number;
    isOnTime?: boolean;
}

interface PurchaseOrderListItemResponse {
    purchaseOrderId: string;
    orderDate: string;
    status: string;
    itemsCount: number;
    totalQuantity: number;
    expiryDate?: string;
    benchmarkDate?: string;
    actualDeliveryDate?: string;
}

interface PurchaseOrderListResponse {
    data: PurchaseOrderListItemResponse[];
    total: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
}

interface SkuRow {
    productId: string;
    name: string;
    sku: string;
    price: number | null;
    currency: string | null;
}

type PaneView = 'details' | 'pos' | 'skus';

/* -------------------------------------------------------------------------- */
/*                              Helper utilities                              */
/* -------------------------------------------------------------------------- */

const formatDateDisplay = (value?: string) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    });
};

const normalizeStatus = (status?: string): string => {
    if (!status) return 'PENDING_REVIEW';
    return status.trim().replace(/\s+/g, '_').toUpperCase();
};

const getStatusMeta = (
    status: string,
): { label: string; type: 'critical' | 'warning' | 'positive' | 'info' | 'neutral' } => {
    const map: Record<string, { label: string; type: 'critical' | 'warning' | 'positive' | 'info' | 'neutral' }> = {
        PENDING_REVIEW: { label: 'Pending Review', type: 'warning' },
        UNDER_PROCESSING: { label: 'Under Processing', type: 'warning' },
        DISPATCHED: { label: 'Dispatched', type: 'positive' },
        IN_PROGRESS: { label: 'In Progress', type: 'info' },
        PARTIALLY_PROCESSED: { label: 'Partially Processed', type: 'info' },
        CANCELLED: { label: 'Cancelled', type: 'critical' },
        PENDING_APPROVAL: { label: 'Pending Approval', type: 'critical' },
        WAITING_FOR_APPROVAL: { label: 'Pending Approval', type: 'critical' },
        APPROVED: { label: 'Approved', type: 'positive' },
        SCHEDULED: { label: 'Scheduled', type: 'info' },
        EXPIRED: { label: 'Expired', type: 'critical' },
        OPEN: { label: 'Open', type: 'neutral' },
        REJECTED: { label: 'Rejected', type: 'critical' },
        COMPLETED: { label: 'Completed', type: 'positive' },
        FULFILLED: { label: 'Fulfilled', type: 'positive' },
        PENDING_ON_SUPPLIER: { label: 'Pending on Supplier', type: 'warning' },
        PAYMENT_PENDING: { label: 'Payment Pending', type: 'warning' },
        PAYMENT_DONE: { label: 'Payment Done', type: 'positive' },
        SHIPPED: { label: 'Shipped', type: 'info' },
        RECEIVED_BY_FF: { label: 'Received by FF', type: 'positive' },
        CREATED: { label: 'Created', type: 'neutral' },
        DRAFT: { label: 'Draft', type: 'neutral' },
        ISSUED: { label: 'Issued', type: 'info' },
        IN_PRODUCTION: { label: 'In Production', type: 'warning' },
        READY_TO_SHIP: { label: 'Ready to Ship', type: 'info' },
        SHIPPED_TO_FF: { label: 'Shipped to FF', type: 'info' },
        SHIPPED_BY_FF: { label: 'Shipped by FF', type: 'info' },
        PENDING_DISPATCH_ON_FF: { label: 'Pending Dispatch on FF', type: 'warning' },
        AT_DESTINATION: { label: 'At Destination', type: 'info' },
        PARTIALLY_RECEIVED: { label: 'Partially Received', type: 'warning' },
        RECEIVED: { label: 'Received', type: 'positive' },
        INVOICE_RAISED: { label: 'Invoice Raised', type: 'warning' },
        INVOICE_DONE_BY_VENDOR: { label: 'Invoice Done by Vendor', type: 'info' },
    };
    if (map[status]) return map[status];
    // Humanize any unmapped status so raw UPPER_SNAKE values never leak to the UI.
    const readable = status
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    return { label: readable, type: 'neutral' };
};

const buildContactName = (firstName?: string, lastName?: string): string => {
    const parts = [firstName, lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '—';
};

const buildLeadTimeLabel = (min?: number | null, max?: number | null): string => {
    if (min == null && max == null) return '—';
    if (min === max || max == null) return `${min ?? max} days`;
    return `${min}–${max} days`;
};

/* -------------------------------------------------------------------------- */
/*                              Vendor Table Cols                              */
/* -------------------------------------------------------------------------- */

const buildVendorColumns = (
    onOpenOrderClick: (row: VendorRow) => void,
): DataSheetColumn<VendorRow>[] => [
    {
        key: 'name',
        header: 'Supplier Name',
        width: 180,
        sticky: true,
        render: (value: string) => (
            <span style={{ fontWeight: 600, color: '#2563eb' }}>
                {value}
            </span>
        ),
    },
    {
        key: 'categories',
        header: 'Categories',
        width: 220,
        render: (value: string[] | null) => {
            if (!value || value.length === 0) return '—';
            return (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {value.map((cat) => (
                        <span
                            key={cat}
                            style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                fontSize: 11,
                                fontWeight: 500,
                                backgroundColor: '#F1F5F9',
                                borderRadius: 4,
                                color: 'rgb(51, 65, 85)',
                            }}
                        >
                            {cat}
                        </span>
                    ))}
                </div>
            );
        },
    },
    {
        key: 'contactPerson',
        header: 'Primary Contact',
        width: 160,
        render: (value: string) => (
            <span style={{ color: 'rgb(71, 85, 105)' }}>{value}</span>
        ),
    },
    {
        key: 'country',
        header: 'Region',
        width: 120,
    },
    {
        key: 'isActive',
        header: 'Status',
        width: 100,
        render: (value: boolean | null) => {
            const isActive = value === true;
            return (
                <DataSheetStatusBadge
                    status={isActive ? 'positive' : 'neutral'}
                    label={isActive ? 'Active' : 'Inactive'}
                />
            );
        },
    },
    {
        key: 'leadTime',
        header: 'Default Lead Time',
        width: 140,
        render: (_value: unknown, row: VendorRow) =>
            buildLeadTimeLabel(row.minLeadTime, row.maxLeadTime),
    },
    {
        key: 'paymentTerms',
        header: 'Payment Terms',
        width: 200,
        render: (value: string | null) => (
            <span style={{ fontSize: 12, color: 'rgb(71, 85, 105)' }}>
                {value || '—'}
            </span>
        ),
    },
    {
        key: 'lastOrderDate',
        header: 'Last Order Date',
        width: 140,
        render: (value: string | null) => formatDateDisplay(value ?? undefined),
    },
    {
        key: 'openOrderCount',
        header: '# Open Orders',
        width: 120,
        align: 'center',
        render: (value: number | null, row: VendorRow) => {
            const count = value ?? 0;
            if (count === 0) return <span style={{ color: 'rgb(148, 163, 184)' }}>0</span>;
            return (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenOrderClick(row);
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#2563eb',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: 13,
                    }}
                >
                    {count}
                </button>
            );
        },
    },
];

/* -------------------------------------------------------------------------- */
/*                              PO Table Cols                                  */
/* -------------------------------------------------------------------------- */

const buildPoColumns = (): DataSheetColumn<VendorPoRow>[] => [
    {
        key: 'poNumber',
        header: 'PO Number',
        width: 180,
        sticky: true,
        render: (value: string) => (
            <span style={{ fontWeight: 600, color: 'rgb(26, 32, 44)', fontFamily: 'monospace', fontSize: 12 }}>
                {value}
            </span>
        ),
    },
    {
        key: 'orderDate',
        header: 'Order Date',
        width: 140,
        sortable: true,
    },
    {
        key: 'status',
        header: 'Status',
        width: 160,
        render: (value: string) => {
            const meta = getStatusMeta(value);
            return <DataSheetStatusBadge status={meta.type} label={meta.label} />;
        },
    },
    {
        key: 'itemsCount',
        header: 'Items',
        width: 80,
        align: 'right',
        render: (value: number) => (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCount(value) ?? '—'}
            </span>
        ),
    },
    {
        key: 'totalQuantity',
        header: 'Qty',
        width: 100,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatCount(value) ?? '—'}
            </span>
        ),
    },
    {
        key: 'expiryDate',
        header: 'Expected Date',
        width: 140,
        render: (value: string) => value || '—',
    },
    {
        key: 'benchmarkDate',
        header: 'Original Planned Date',
        width: 140,
        render: (value: string) => value || '—',
    },
    {
        key: 'actualDeliveryDate',
        header: 'Actual Delivery',
        width: 140,
        render: (value: string) => value || '—',
    },
    {
        key: 'varianceDays',
        header: 'Variance (days)',
        width: 130,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => {
            if (value === undefined) return '—';
            const color = value > 0 ? '#E53E3E' : '#38A169';
            const prefix = value > 0 ? '+' : '';
            return (
                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color }}>
                    {prefix}{value}
                </span>
            );
        },
    },
    {
        key: 'isOnTime',
        header: 'On-Time',
        width: 100,
        render: (value: boolean | undefined) => {
            if (value === undefined) return '—';
            return (
                <span style={{ fontWeight: 600, color: value ? '#38A169' : '#E53E3E' }}>
                    {value ? 'Yes' : 'No'}
                </span>
            );
        },
    },
];

/* -------------------------------------------------------------------------- */
/*                           SKU Table Cols                                     */
/* -------------------------------------------------------------------------- */

const SKU_COLUMNS: DataSheetColumn<SkuRow>[] = [
    {
        key: 'sku',
        header: 'SKU ID',
        width: 200,
        sticky: true,
        render: (value: string) => (
            <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>
                {value || '—'}
            </span>
        ),
    },
    {
        key: 'name',
        header: 'SKU Name',
        width: 300,
        render: (value: string) => (
            <span style={{ fontWeight: 500, color: 'rgb(26, 32, 44)' }}>
                {value || '—'}
            </span>
        ),
    },
    {
        key: 'price',
        header: 'Price',
        width: 120,
        align: 'right',
        render: (value: number | null, row: SkuRow) => {
            if (value == null) return '—';
            const symbol = row.currency === 'USD' ? '$' : row.currency ?? '';
            return (
                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {symbol}{value.toFixed(2)}
                </span>
            );
        },
    },
];

/* -------------------------------------------------------------------------- */
/*                           Section label style                               */
/* -------------------------------------------------------------------------- */

const sectionLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: 'rgb(100, 116, 139)',
    marginBottom: 12,
};

const fieldLabelStyle: React.CSSProperties = {
    fontSize: 12,
    color: 'rgb(100, 116, 139)',
    marginBottom: 2,
};

const fieldValueStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    color: 'rgb(26, 32, 44)',
};

/* -------------------------------------------------------------------------- */
/*                           Main Component                                   */
/* -------------------------------------------------------------------------- */

const SuppliersPage: React.FC = () => {
    const [selectedPoRow, setSelectedPoRow] =
        useState<PurchaseOrderRow | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedVendorId = searchParams.get('vendorId');
    const { currentUser } = useCurrentUser();
    const isConsultant = currentUser?.role === 'CONSULTANT';
    const [shareModal, setShareModal] = useState<{ resourceKey: string } | null>(null);

    const { captureContext: captureVendors, addToChat: addVendorsToChat } = useWidgetContextCapture({
        componentName: 'SupplierListTable',
        label: 'Supplier & Vendor List',
    });

    /* ---- Vendor list state ---- */
    const [vendors, setVendors] = useState<VendorRow[]>([]);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [vendorSearch, setVendorSearch] = useState('');
    const [vendorStatusFilter, setVendorStatusFilter] = useState('all');
    const [vendorNameFilter, setVendorNameFilter] = useState('all');
    const [vendorPage, setVendorPage] = useState(1);
    const [vendorPageSize, setVendorPageSize] = useState(20);
    const [vendorTotal, setVendorTotal] = useState(0);

    /* ---- Vendor detail state ---- */
    const [vendorDetail, setVendorDetail] = useState<VendorDetailApiResponse | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    /* ---- Pane view state ---- */
    const [paneView, setPaneView] = useState<PaneView>('details');

    /* ---- PO table state (inside vendor pane) ---- */
    const [vendorPos, setVendorPos] = useState<VendorPoRow[]>([]);
    const [loadingPos, setLoadingPos] = useState(false);
    const [poTotal, setPoTotal] = useState(0);
    const [poPage, setPoPage] = useState(1);
    const [poPageSize, setPoPageSize] = useState(20);
    const [poStatusFilter, setPoStatusFilter] = useState('all');
    const [poStatusOptions, setPoStatusOptions] = useState<DropdownOption[]>([
        { label: 'All Status', value: 'all' },
    ]);

    /* ---- Lanes state ---- */
    const [vendorLanes, setVendorLanes] = useState<LaneRow[]>([]);
    const [loadingLanes, setLoadingLanes] = useState(false);
    const cbmEnabled = useCbmEnabled();
    const [laneModal, setLaneModal] = useState<{ open: boolean; lane: LaneRow | null }>({ open: false, lane: null });
    // Bumped after a lane is saved so the lanes effect below re-reads the vendor.
    const [lanesVersion, setLanesVersion] = useState(0);
    const laneColumns = useMemo(
        () => buildLaneColumns(cbmEnabled, (lane) => setLaneModal({ open: true, lane })),
        [cbmEnabled],
    );

    /* ---- SKU state ---- */
    const [vendorSkus, setVendorSkus] = useState<SkuRow[]>([]);
    const [loadingSkus, setLoadingSkus] = useState(false);

    /* -------------------------------------------------------------------------- */
    /*                            Pane open / close                              */
    /* -------------------------------------------------------------------------- */

    const openVendorPane = useCallback(
        (row: VendorRow) => {
            setSearchParams({ vendorId: row.vendorId });
            setPaneView('details');
        },
        [setSearchParams],
    );

    const openVendorPaneToPos = useCallback(
        (row: VendorRow) => {
            setSearchParams({ vendorId: row.vendorId });
            setPaneView('pos');
            setPoStatusFilter('all');
        },
        [setSearchParams],
    );

    const closeVendorPane = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('vendorId');
            return next;
        });
    }, [setSearchParams]);

    /* -------------------------------------------------------------------------- */
    /*                            Vendor list fetch                              */
    /* -------------------------------------------------------------------------- */

    useEffect(() => {
        const fetchVendors = async () => {
            setLoadingVendors(true);
            try {
                const response = (await ReparoApi({
                    method: 'GET',
                    url: '/vendors',
                    body: undefined,
                })) as VendorApiResponse[] | null;

                const rows: VendorRow[] = (response ?? [])
                    .filter((v): v is VendorApiResponse & { vendorId: string } =>
                        Boolean(v.vendorId),
                    )
                    .map((v) => ({
                        vendorId: v.vendorId!,
                        name: v.name ?? '—',
                        country: v.country ?? '—',
                        city: v.city,
                        minLeadTime: v.minLeadTime ?? null,
                        maxLeadTime: v.maxLeadTime ?? null,
                        type: v.type ?? '—',
                        contactPerson: v.contactPerson ?? '—',
                        email: v.email,
                        categories: v.categories,
                        paymentTerms: v.paymentTerms,
                        isActive: v.isActive,
                        vendorRole: v.vendorRole,
                        lastOrderDate: v.lastOrderDate,
                        openOrderCount: v.openOrderCount,
                    }));

                setVendors(rows);
                setVendorTotal(rows.length);
                captureVendors({ page: vendorPage, pageSize: vendorPageSize });
            } catch {
                setVendors([]);
            } finally {
                setLoadingVendors(false);
            }
        };

        void fetchVendors();
    }, [vendorPage, vendorPageSize]);

    /* -------------------------------------------------------------------------- */
    /*                           Vendor detail fetch                             */
    /* -------------------------------------------------------------------------- */

    useEffect(() => {
        if (!selectedVendorId) {
            setVendorDetail(null);
            setVendorPos([]);
            setVendorLanes([]);
            setVendorSkus([]);
            setPoPage(1);
            setPoStatusFilter('all');
            setPaneView('details');
            return;
        }

        const fetchDetail = async () => {
            setLoadingDetail(true);
            try {
                const v = (await ReparoApi({
                    method: 'GET',
                    url: `/vendors/${selectedVendorId}`,
                    body: undefined,
                })) as VendorDetailApiResponse | null;

                if (v) {
                    setVendorDetail(v);
                }
            } catch {
                setVendorDetail(null);
            } finally {
                setLoadingDetail(false);
            }
        };

        void fetchDetail();
    }, [selectedVendorId]);

    /* -------------------------------------------------------------------------- */
    /*                         Lanes fetch (for detail pane)                     */
    /* -------------------------------------------------------------------------- */

    useEffect(() => {
        if (!selectedVendorId) return;

        const fetchLanes = async () => {
            setLoadingLanes(true);
            try {
                const response = (await ReparoApi({
                    method: 'GET',
                    url: `/vendors/${selectedVendorId}/lanes`,
                    body: undefined,
                })) as Array<{
                    laneId?: string;
                    warehouseId?: string;
                    source?: string;
                    destination?: string;
                    productionTime?: number;
                    qcTime?: number;
                    transitTime?: number;
                    deliveryTime?: number;
                    mode?: string;
                    maxCbmPerShipment?: number | null;
                    minFillPct?: number | null;
                }> | null;

                setVendorLanes(
                    (response ?? []).map((l) => ({
                        laneId: l.laneId,
                        warehouseId: l.warehouseId ?? '',
                        source: l.source ?? '—',
                        destination: l.destination ?? '—',
                        productionTime: l.productionTime ?? null,
                        qcTime: l.qcTime ?? null,
                        transitTime: l.transitTime ?? null,
                        deliveryTime: l.deliveryTime ?? null,
                        mode: l.mode ?? null,
                        maxCbmPerShipment: l.maxCbmPerShipment ?? null,
                        minFillPct: l.minFillPct ?? null,
                    })),
                );
            } catch {
                setVendorLanes([]);
            } finally {
                setLoadingLanes(false);
            }
        };

        void fetchLanes();
    }, [selectedVendorId, lanesVersion]);

    /* -------------------------------------------------------------------------- */
    /*                         SKU fetch (for detail pane)                       */
    /* -------------------------------------------------------------------------- */

    useEffect(() => {
        if (!selectedVendorId) return;

        const fetchSkus = async () => {
            setLoadingSkus(true);
            try {
                const response = (await ReparoApi({
                    method: 'POST',
                    url: '/products/by-vendor',
                    body: JSON.stringify({ vendorId: selectedVendorId }),
                })) as SkuRow[] | null;

                setVendorSkus(response ?? []);
            } catch {
                setVendorSkus([]);
            } finally {
                setLoadingSkus(false);
            }
        };

        void fetchSkus();
    }, [selectedVendorId]);

    /* -------------------------------------------------------------------------- */
    /*                         PO status options fetch                           */
    /* -------------------------------------------------------------------------- */

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const statuses = (await ReparoApi({
                    method: 'POST',
                    url: '/purchase-orders/statuses/list',
                    body: JSON.stringify({ type: 'INCOMING' }),
                })) as unknown as string[] | null;

                const mapped = (statuses ?? [])
                    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
                    .map((s) => ({ label: getStatusMeta(normalizeStatus(s)).label, value: s }));

                setPoStatusOptions([
                    { label: 'All Status', value: 'all' },
                    ...mapped,
                ]);
            } catch {
                setPoStatusOptions([{ label: 'All Status', value: 'all' }]);
            }
        };

        void fetchStatuses();
    }, []);

    /* -------------------------------------------------------------------------- */
    /*                           PO list fetch (by vendor)                       */
    /* -------------------------------------------------------------------------- */

    useEffect(() => {
        if (!selectedVendorId) return;

        const fetchPos = async () => {
            setLoadingPos(true);
            try {
                const payload = {
                    type: 'INCOMING',
                    vendors: [selectedVendorId],
                    status: poStatusFilter === 'all' ? [] : [poStatusFilter],
                    page: poPage,
                    pageSize: poPageSize,
                    startDate: '2000-01-01',
                    endDate: new Date().toISOString().split('T')[0],
                };

                const response = (await ReparoApi({
                    method: 'POST',
                    url: '/purchase-orders/list',
                    body: JSON.stringify(payload),
                })) as unknown as PurchaseOrderListResponse | null;

                const rows: VendorPoRow[] = (response?.data ?? []).map((item) => {
                    let varianceDays: number | undefined;
                    let isOnTime: boolean | undefined;
                    if (item.benchmarkDate && item.actualDeliveryDate) {
                        const benchmark = new Date(item.benchmarkDate).getTime();
                        const actual = new Date(item.actualDeliveryDate).getTime();
                        varianceDays = Math.ceil((actual - benchmark) / (1000 * 60 * 60 * 24));
                        isOnTime = varianceDays <= 0;
                    }
                    return {
                        id: item.purchaseOrderId,
                        poNumber: item.purchaseOrderId,
                        orderDate: formatDateDisplay(item.orderDate),
                        status: normalizeStatus(item.status),
                        itemsCount: item.itemsCount ?? 0,
                        totalQuantity: item.totalQuantity ?? 0,
                        expiryDate: item.expiryDate
                            ? formatDateDisplay(item.expiryDate)
                            : undefined,
                        benchmarkDate: item.benchmarkDate
                            ? formatDateDisplay(item.benchmarkDate)
                            : undefined,
                        actualDeliveryDate: item.actualDeliveryDate
                            ? formatDateDisplay(item.actualDeliveryDate)
                            : undefined,
                        varianceDays,
                        isOnTime,
                    };
                });

                setVendorPos(rows);
                setPoTotal(response?.total ?? rows.length);
            } catch {
                setVendorPos([]);
            } finally {
                setLoadingPos(false);
            }
        };

        void fetchPos();
    }, [selectedVendorId, poStatusFilter, poPage, poPageSize]);

    /* -------------------------------------------------------------------------- */
    /*                           Filtered vendor list                            */
    /* -------------------------------------------------------------------------- */

    const filteredVendors = useMemo(() => {
        const q = vendorSearch.trim().toLowerCase();
        return vendors.filter((v) => {
            if (vendorNameFilter !== 'all' && v.vendorId !== vendorNameFilter) {
                return false;
            }
            if (vendorStatusFilter !== 'all') {
                const isActive = v.isActive === true;
                if (vendorStatusFilter === 'active' && !isActive) return false;
                if (vendorStatusFilter === 'inactive' && isActive) return false;
            }
            if (q) {
                const matchesSearch =
                    v.name.toLowerCase().includes(q) ||
                    v.country.toLowerCase().includes(q) ||
                    v.contactPerson.toLowerCase().includes(q) ||
                    (v.categories ?? []).some((c) =>
                        c.toLowerCase().includes(q),
                    );
                if (!matchesSearch) return false;
            }
            return true;
        });
    }, [vendors, vendorSearch, vendorStatusFilter, vendorNameFilter]);

    /* ---- Filter dropdown options ---- */
    const vendorStatusOptions = useMemo<DropdownOption[]>(
        () => [
            { label: 'All Status', value: 'all' },
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
        ],
        [],
    );

    const vendorNameOptions = useMemo<DropdownOption[]>(
        () => [
            { label: 'All Suppliers', value: 'all' },
            ...vendors
                .map((v) => ({ label: v.name, value: v.vendorId }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [vendors],
    );

    /* -------------------------------------------------------------------------- */
    /*                           Vendor detail tags                              */
    /* -------------------------------------------------------------------------- */

    const vendorTags = useMemo(() => {
        if (!vendorDetail) return [];
        const tags = [];
        if (vendorDetail.vendorRole) {
            tags.push({ label: 'Role', value: vendorDetail.vendorRole });
        }
        if (vendorDetail.country && vendorDetail.country !== '—') {
            tags.push({ label: 'Country', value: vendorDetail.country });
        }
        if (vendorDetail.categories && vendorDetail.categories.length > 0) {
            tags.push({ label: 'Categories', value: vendorDetail.categories.join(', ') });
        }
        return tags;
    }, [vendorDetail]);

    /* -------------------------------------------------------------------------- */
    /*                              PO row click                                 */
    /* -------------------------------------------------------------------------- */

    const handlePoRowClick = useCallback(
        (row: VendorPoRow) => {
            setSelectedPoRow({
                id: row.id,
                poNumber: row.poNumber,
                totalQuantity: row.totalQuantity,
                orderDate: row.orderDate,
                expiryDate: row.expiryDate,
                status: row.status,
                side: 'supply',
                vendor: vendorDetail?.name,
            });
        },
        [vendorDetail?.name],
    );

    /* -------------------------------------------------------------------------- */
    /*                                 Columns                                   */
    /* -------------------------------------------------------------------------- */

    const vendorColumns = useMemo(() => buildVendorColumns(openVendorPaneToPos), [openVendorPaneToPos]);
    const poColumns = useMemo(() => buildPoColumns(), []);

    /* -------------------------------------------------------------------------- */
    /*                          Derived: port of loading                          */
    /* -------------------------------------------------------------------------- */

    const portOfLoading = useMemo(() => {
        if (vendorLanes.length === 0) return '—';
        const sources = [...new Set(vendorLanes.map((l) => l.source).filter((s) => s && s !== '—'))];
        return sources.length > 0 ? sources.join(', ') : '—';
    }, [vendorLanes]);

    /* -------------------------------------------------------------------------- */
    /*                                 Render                                    */
    /* -------------------------------------------------------------------------- */

    // Lead times from the list row (lane-based)
    const listRow = vendors.find((v) => v.vendorId === selectedVendorId);

    return (
        <div className="flex h-full w-full flex-col page-shell">
            <PageHeader
                icon="🏭"
                title="Suppliers"
                subtitle="Manage your supplier relationships and information"
                actions={<AskAiButton />}
            />

            {/* Supplier overview metrics */}
            <div style={{ marginBottom: 24 }}>
                <MetricStrip instances={SUPPLIER_LIST_METRICS} />
            </div>

            {/* Vendor table */}
            <DataSheet<VendorRow>
                className="flex-1 min-h-0"
                data={filteredVendors}
                columns={vendorColumns}
                title="Suppliers"
                subtitle={
                    vendorTotal > 0
                        ? `${vendorTotal} supplier${vendorTotal !== 1 ? 's' : ''}`
                        : undefined
                }
                enableSearch
                searchPlaceholder="Search by name, country, category..."
                searchValue={vendorSearch}
                onSearchChange={setVendorSearch}
                toolbarControls={
                    <div className="flex items-center gap-2">
                        <FilterDropdown
                            value={vendorNameFilter}
                            onChange={setVendorNameFilter}
                            options={vendorNameOptions}
                            placeholder="Supplier"
                        />
                        <FilterDropdown
                            value={vendorStatusFilter}
                            onChange={setVendorStatusFilter}
                            options={vendorStatusOptions}
                            placeholder="Status"
                        />
                    </div>
                }
                isLoading={loadingVendors}
                getRowKey={(row) => row.vendorId}
                onRowClick={openVendorPane}
                emptyState="No suppliers found."
                maxHeight="auto"
                onShare={isConsultant ? undefined : () => setShareModal({ resourceKey: ConsultantCapability.TABLE_SUPPLIERS_VENDOR_LIST })}
                onAddToChat={addVendorsToChat}
                footer={
                    <DataSheetPagination
                        currentPage={vendorPage}
                        totalPages={Math.ceil(vendorTotal / vendorPageSize) || 1}
                        pageSize={vendorPageSize}
                        totalItems={vendorTotal}
                        pageSizeOptions={[20, 50, 100]}
                        onPageChange={setVendorPage}
                        onPageSizeChange={(size) => {
                            setVendorPage(1);
                            setVendorPageSize(size);
                        }}
                    />
                }
            />

            {/* ----------------------------------------------------------------- */}
            {/*  Vendor detail pane — 80% width fixed overlay                    */}
            {/* ----------------------------------------------------------------- */}
            {selectedVendorId && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 30,
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-end',
                        backgroundColor: 'rgba(15, 23, 42, 0.10)',
                        backdropFilter: 'blur(2px)',
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeVendorPane();
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            width: '80%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)',
                            overflow: 'hidden',
                        }}
                    >
                        {/* ---- Pane header ---- */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 16,
                                padding: '20px 24px',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                flexShrink: 0,
                                backgroundColor: '#FFFFFF',
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                                {loadingDetail ? (
                                    <div style={{ width: 200, height: 22, borderRadius: 4, backgroundColor: 'rgba(0,0,0,0.06)' }} />
                                ) : (
                                    <>
                                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'rgb(26, 32, 44)', lineHeight: 1.3 }}>
                                            {vendorDetail?.name ?? '—'}
                                        </h2>
                                        <EntityTagStrip tags={vendorTags} />
                                    </>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={closeVendorPane}
                                aria-label="Close vendor details"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: '50%',
                                    color: 'rgb(100, 116, 139)',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                <XCircle size={18} />
                            </button>
                        </div>

                        {/* ---- View switcher ---- */}
                        <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
                            <SegmentedToggle
                                value={paneView}
                                onChange={(v) => setPaneView(v as PaneView)}
                                options={[
                                    { label: 'Vendor Details', value: 'details' },
                                    { label: 'View All POs', value: 'pos' },
                                    { label: 'View SKUs', value: 'skus' },
                                ]}
                            />
                        </div>

                        {/* ---- Pane body (scrollable) ---- */}
                        <div
                            style={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'auto',
                                padding: '20px 24px 24px',
                            }}
                        >

                            {/* ===== DETAILS VIEW ===== */}
                            {paneView === 'details' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

                                    {/* -- Key Metrics band -- */}
                                    <div>
                                        <div style={sectionLabelStyle}>Key Metrics (Last 12 months)</div>
                                        <MetricStrip
                                            instances={VENDOR_PANE_METRICS}
                                            context={selectedVendorId ? { vendorId: selectedVendorId } : undefined}
                                        />
                                    </div>

                                    {/* -- Overview section -- */}
                                    <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 20 }}>
                                        <div style={sectionLabelStyle}>Overview</div>
                                        <div style={{ display: 'flex', gap: 40 }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>Legal Name</div>
                                                <div style={fieldValueStyle}>{vendorDetail?.name ?? '—'}</div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>Supplier Code</div>
                                                <div style={fieldValueStyle}>{vendorDetail?.vendorCode ?? '—'}</div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>Categories</div>
                                                <div style={fieldValueStyle}>
                                                    {vendorDetail?.categories?.join(', ') || '—'}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1.4 }}>
                                                <div style={fieldLabelStyle}>Capabilities</div>
                                                {selectedVendorId && (
                                                    <VendorCapabilitiesField
                                                        vendorId={selectedVendorId}
                                                        capabilities={(vendorDetail?.capabilities ?? null) as VendorCapability[] | null}
                                                        onSaved={(next) =>
                                                            setVendorDetail((prev) => (prev ? { ...prev, capabilities: next } : prev))
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>PO number prefix</div>
                                                {selectedVendorId && (
                                                    <VendorPoPrefixField
                                                        vendorId={selectedVendorId}
                                                        poNumberPrefix={vendorDetail?.poNumberPrefix ?? null}
                                                        onSaved={(next) =>
                                                            setVendorDetail((prev) => (prev ? { ...prev, poNumberPrefix: next } : prev))
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>Port of Loading</div>
                                                <div style={fieldValueStyle}>{portOfLoading}</div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>City</div>
                                                <div style={fieldValueStyle}>{vendorDetail?.city ?? '—'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* -- Commercial Terms section -- */}
                                    <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 20 }}>
                                        <div style={sectionLabelStyle}>Commercial Terms</div>
                                        <div style={{ display: 'flex', gap: 40 }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>Payment Terms</div>
                                                <div style={fieldValueStyle}>
                                                    {vendorDetail?.paymentTerms || '—'}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>Currency and Incoterms</div>
                                                <div style={fieldValueStyle}>
                                                    {[
                                                        vendorSkus.length > 0 ? vendorSkus[0].currency : null,
                                                        vendorDetail?.incoterms,
                                                    ].filter(Boolean).join(' / ') || '—'}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={fieldLabelStyle}>MOQ</div>
                                                <div style={fieldValueStyle}>
                                                    {vendorDetail?.moq != null ? `${formatCount(vendorDetail.moq)} units` : '—'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* -- Production Capacity section (manufacturers only) -- */}
                                    {selectedVendorId && vendorDetail?.capabilities?.includes('MANUFACTURER') && (
                                        <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 20 }}>
                                            <div style={sectionLabelStyle}>Production Capacity</div>
                                            <VendorProductionCapacity
                                                vendorId={selectedVendorId}
                                                dailyProductionCapacity={vendorDetail.dailyProductionCapacity ?? null}
                                                workingDays={vendorDetail.workingDays ?? null}
                                                onSaved={(next) =>
                                                    setVendorDetail((prev) => (prev ? { ...prev, ...next } : prev))
                                                }
                                            />
                                        </div>
                                    )}

                                    {/* -- Lead Time and Logistics section -- */}
                                    <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 20 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={sectionLabelStyle}>Lead Time and Logistics</div>
                                            <Button variant="subtle" size="xs" onClick={() => setLaneModal({ open: true, lane: null })}>
                                                + Add lane
                                            </Button>
                                        </div>
                                        <DataSheet<LaneRow>
                                            data={vendorLanes}
                                            columns={laneColumns}
                                            isLoading={loadingLanes}
                                            getRowKey={(row, idx) => row.laneId ?? String(idx)}
                                            emptyState="No lane data available for this vendor."
                                            bare
                                            hideHeaderBar
                                        />
                                        <LaneEditModal
                                            opened={laneModal.open}
                                            vendorId={selectedVendorId}
                                            lane={laneModal.lane}
                                            cbmEnabled={cbmEnabled}
                                            onClose={() => setLaneModal({ open: false, lane: null })}
                                            onSaved={() => setLanesVersion((v) => v + 1)}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ===== POs VIEW ===== */}
                            {paneView === 'pos' && (
                                <DataSheet<VendorPoRow>
                                    className="flex-1 min-h-0"
                                    data={vendorPos}
                                    columns={poColumns}
                                    title="Purchase Orders"
                                    subtitle={
                                        poTotal > 0
                                            ? `${poTotal} order${poTotal !== 1 ? 's' : ''}`
                                            : undefined
                                    }
                                    isLoading={loadingPos}
                                    getRowKey={(row) => row.id}
                                    onRowClick={handlePoRowClick}
                                    emptyState="No purchase orders found for this supplier."
                                    maxHeight="auto"
                                    toolbarControls={
                                        <FilterDropdown
                                            value={poStatusFilter}
                                            onChange={(val) => {
                                                setPoStatusFilter(val);
                                                setPoPage(1);
                                            }}
                                            options={poStatusOptions}
                                            placeholder="Status"
                                        />
                                    }
                                    footer={
                                        <DataSheetPagination
                                            currentPage={poPage}
                                            totalPages={Math.ceil(poTotal / poPageSize) || 1}
                                            pageSize={poPageSize}
                                            totalItems={poTotal}
                                            pageSizeOptions={[20, 50, 100]}
                                            onPageChange={setPoPage}
                                            onPageSizeChange={(size) => {
                                                setPoPage(1);
                                                setPoPageSize(size);
                                            }}
                                        />
                                    }
                                />
                            )}

                            {/* ===== SKUs VIEW ===== */}
                            {paneView === 'skus' && (
                                <DataSheet<SkuRow>
                                    className="flex-1 min-h-0"
                                    data={vendorSkus}
                                    columns={SKU_COLUMNS}
                                    title="SKUs and Pricing"
                                    subtitle={
                                        vendorSkus.length > 0
                                            ? `${vendorSkus.length} SKU${vendorSkus.length !== 1 ? 's' : ''}`
                                            : undefined
                                    }
                                    isLoading={loadingSkus}
                                    getRowKey={(row) => row.productId}
                                    emptyState="No SKUs mapped to this vendor."
                                    maxHeight="auto"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {shareModal && (
                <ShareModal
                    opened={!!shareModal}
                    onClose={() => setShareModal(null)}
                    resourceKey={shareModal.resourceKey}
                    merchantId={currentUser?.organizationId ?? ''}
                />
            )}

            <PurchaseOrderDetailDrawer
                poId={selectedPoRow?.id ?? null}
                initialRow={selectedPoRow ?? undefined}
                side="supply"
                onClose={() => setSelectedPoRow(null)}
            />
        </div>
    );
};

export default SuppliersPage;
